import React, { useState, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  TextField,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Stack,
  Chip,
  InputAdornment,
  Fade,
  Backdrop,
  Card,
  CardContent,
  Collapse,
  Tooltip,
  Alert
} from '@mui/material';
import {
  Category as CategoryIcon,
  EditNote as EditIcon,
  DeleteOutline as DeleteIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Code as CodeIcon
} from '@mui/icons-material';

export default function AdministrarTiposMensajeDialog({
  open,
  onClose,
  tiposMensaje = [],
  selectedTipoMensaje,
  setSelectedTipoMensaje,
  newTipoMensaje,
  setNewTipoMensaje,
  showTipoMensajeForm,
  setShowTipoMensajeForm,
  onSaveTipoMensaje,
  onDeleteTipoMensaje
}) {
  
  // Estados para búsqueda y filtros
  const [searchTerm, setSearchTerm] = useState('');

  // Filtrar tipos de mensaje
  const filteredTipos = useMemo(() => {
    if (!searchTerm) return tiposMensaje;
    
    return tiposMensaje.filter(tipo =>
      tipo.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tipo.descripcion?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tipo.codigo.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [tiposMensaje, searchTerm]);

  // Callbacks optimizados
  const handleClose = useCallback(() => {
    onClose();
    setSearchTerm('');
  }, [onClose]);

  const handleCreateNew = useCallback(() => {
    setSelectedTipoMensaje(null);
    setNewTipoMensaje({ nombre: "", descripcion: "" });
    setShowTipoMensajeForm(true);
  }, [setSelectedTipoMensaje, setNewTipoMensaje, setShowTipoMensajeForm]);

  const handleEdit = useCallback((tipo) => {
    setSelectedTipoMensaje(tipo);
    setNewTipoMensaje({
      nombre: tipo.nombre,
      descripcion: tipo.descripcion,
      codigo: tipo.codigo
    });
    setShowTipoMensajeForm(true);
  }, [setSelectedTipoMensaje, setNewTipoMensaje, setShowTipoMensajeForm]);

  const handleCancel = useCallback(() => {
    setSelectedTipoMensaje(null);
    setNewTipoMensaje({ nombre: "", descripcion: "" });
    setShowTipoMensajeForm(false);
  }, [setSelectedTipoMensaje, setNewTipoMensaje, setShowTipoMensajeForm]);

  const generateCode = useCallback((nombre) => {
    return nombre
      ? nombre.toUpperCase().replace(/[^A-Z0-9]/g, '_').substring(0, 40)
      : "";
  }, []);

  // Componente para renderizar fila de tipo
  const TipoRow = React.memo(({ tipo }) => (
    <TableRow 
      sx={{ 
        '&:last-child td, &:last-child th': { border: 0 },
        '&:hover': { background: '#f8fafc' },
        transition: 'all 0.2s ease'
      }}
    >
      <TableCell sx={{ fontWeight: 600, color: "rgba(30,41,59,0.98)" }}>
        <Chip 
          label={tipo.codigo}
          size="small"
          icon={<CodeIcon sx={{ fontSize: 14 }} />}
          sx={{
            background: "rgba(30,41,59,0.1)",
            color: "rgba(30,41,59,0.98)",
            fontWeight: 600,
            fontSize: 11
          }}
        />
      </TableCell>
      <TableCell sx={{ fontWeight: 600, color: "rgba(30,41,59,0.98)" }}>
        {tipo.nombre}
      </TableCell>
      <TableCell sx={{ color: "#64748b", maxWidth: 200 }}>
        <Typography 
          variant="body2" 
          sx={{ 
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {tipo.descripcion || "Sin descripción"}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <Tooltip title="Editar tipo">
            <IconButton 
              size="small" 
              onClick={() => handleEdit(tipo)}
              sx={{ 
                color: "#6366f1",
                "&:hover": { background: "#6366f108" }
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Eliminar tipo">
            <IconButton 
              size="small"
              onClick={() => onDeleteTipoMensaje(tipo)}
              sx={{ 
                color: "#ef4444",
                "&:hover": { background: "#ef444408" }
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </TableCell>
    </TableRow>
  ));

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      TransitionComponent={Fade}
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 300,
        sx: { backgroundColor: 'rgba(0,0,0,0.7)' }
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
          boxShadow: "0 25px 80px rgba(99,102,241,0.2)",
          border: "1px solid rgba(99,102,241,0.1)",
          overflow: "hidden",
          maxHeight: '90vh',
          width: '90vw',
          maxWidth: 900
        }
      }}
    >
      {/* Header con el mismo estilo */}
      <DialogTitle sx={{ 
        background: "rgba(30,41,59,0.98)",
        color: "#fff",
        display: "flex", 
        alignItems: "center", 
        justifyContent: "space-between",
        py: 1.25,
        px: 2.5,
        minHeight: 55
      }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <CategoryIcon sx={{ fontSize: 22 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 17 }}>
            Tipos de Mensaje
          </Typography>
          <Chip 
            label={`${filteredTipos.length}/${tiposMensaje.length}`}
            size="small"
            sx={{ 
              background: "rgba(255,255,255,0.2)", 
              color: "#fff", 
              fontWeight: 600 
            }}
          />
        </Stack>
        
        <IconButton 
          onClick={handleClose}
          size="small"
          sx={{ 
            color: "#fff", 
            background: "rgba(255,255,255,0.15)",
            "&:hover": { background: "rgba(255,255,255,0.25)" }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: 'calc(90vh - 55px)' }}>
        {/* Descripción y barra de acciones */}
        <Box sx={{ 
          p: 2, 
          background: "#fff", 
          borderBottom: "1px solid #e0e7ef",
          flexShrink: 0
        }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: "#64748b", mb: 1 }}>
                Los tipos de mensaje te permiten categorizar y dar formato a tus mensajes en los flujos de conversación.
              </Typography>
              
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar tipos de mensaje..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#6b7280", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchTerm('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setSearchTerm('')}
                  startIcon={<ClearIcon />}
                  sx={{ 
                    textTransform: "none",
                    borderColor: "rgba(30,41,59,0.3)",
                    color: "rgba(30,41,59,0.98)",
                    "&:hover": { 
                      borderColor: "rgba(30,41,59,0.98)",
                      backgroundColor: "rgba(30,41,59,0.05)",
                      color: "rgba(30,41,59,0.98)"
                    }
                  }}
                >
                  Limpiar
                </Button>
                
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleCreateNew}
                  sx={{
                    background: "rgba(30,41,59,0.98)",
                    fontWeight: 600,
                    textTransform: "none",
                    "&:hover": { 
                      background: "rgba(30,41,59,0.85)" 
                    }
                  }}
                >
                  Nuevo tipo
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
        
        {/* Formulario colapsable */}
        <Collapse in={selectedTipoMensaje !== null || showTipoMensajeForm}>
          <Box sx={{ p: 2, background: "#f8fafc", borderBottom: "1px solid #e0e7ef", flexShrink: 0 }}>
            <Card sx={{ 
              borderRadius: 2, 
              border: "2px solid #6366f1",
              boxShadow: "0 8px 32px rgba(99,102,241,0.12)"
            }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="h6" sx={{ color: "#6366f1", fontWeight: 700, fontSize: 16 }}>
                    {selectedTipoMensaje ? "✏️ Editar tipo de mensaje" : "➕ Nuevo tipo de mensaje"}
                  </Typography>
                  <IconButton onClick={handleCancel} size="small">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Código"
                      fullWidth
                      size="small"
                      value={selectedTipoMensaje ? selectedTipoMensaje.codigo : generateCode(newTipoMensaje.nombre)}
                      disabled
                      helperText="Se genera automáticamente"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CodeIcon sx={{ color: "#6b7280", fontSize: 18 }} />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Nombre"
                      fullWidth
                      size="small"
                      value={newTipoMensaje.nombre}
                      onChange={e => setNewTipoMensaje({...newTipoMensaje, nombre: e.target.value})}
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Descripción"
                      fullWidth
                      size="small"
                      value={newTipoMensaje.descripcion}
                      onChange={e => setNewTipoMensaje({...newTipoMensaje, descripcion: e.target.value})}
                    />
                  </Grid>
                  
                  {/* Vista previa del código */}
                  {newTipoMensaje.nombre && (
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, border: '1px dashed #ccc', borderRadius: 2, background: '#f8fafc' }}>
                        <Typography variant="caption" sx={{ color: '#6b7280', mb: 1, display: 'block' }}>
                          Vista previa del código:
                        </Typography>
                        <Chip 
                          label={generateCode(newTipoMensaje.nombre)}
                          size="small"
                          icon={<CodeIcon sx={{ fontSize: 14 }} />}
                          sx={{
                            background: "rgba(30,41,59,0.1)",
                            color: "rgba(30,41,59,0.98)",
                            fontWeight: 600
                          }}
                        />
                      </Box>
                    </Grid>
                  )}
                  
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button 
                        variant="outlined"
                        onClick={handleCancel}
                        size="small"
                        sx={{ textTransform: "none" }}
                      >
                        Cancelar
                      </Button>
                      <Button 
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={onSaveTipoMensaje}
                        disabled={!newTipoMensaje.nombre.trim()}
                        size="small"
                        sx={{ 
                          textTransform: "none",
                          background: "linear-gradient(135deg, #10b981 0%, #16a34a 100%)",
                          fontWeight: 600
                        }}
                      >
                        {selectedTipoMensaje ? 'Actualizar' : 'Crear'}
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        </Collapse>
        
        {/* Tabla de tipos de mensaje */}
        <Box sx={{ 
          flex: 1,
          p: 2, 
          overflowY: "auto",
          background: "#f8fafc"
        }}>
          {filteredTipos.length === 0 ? (
            <Box sx={{ 
              textAlign: "center", 
              py: 8,
              color: "#6b7280"
            }}>
              <CategoryIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
              <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
                {searchTerm 
                  ? "No se encontraron tipos" 
                  : "No hay tipos registrados"
                }
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {searchTerm 
                  ? "Intenta modificar el término de búsqueda"
                  : "Comienza creando tu primer tipo de mensaje"
                }
              </Typography>
              {!searchTerm && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateNew}
                  sx={{
                    background: "rgba(30,41,59,0.98)",
                    color: "#fff",
                    fontWeight: 600,
                    textTransform: "none",
                    px: 4,
                    "&:hover": { 
                      background: "rgba(30,41,59,0.85)" 
                    }
                  }}
                >
                  Crear primer tipo
                </Button>
              )}
            </Box>
          ) : (
            <Card sx={{ 
              borderRadius: 2,
              border: "1px solid #e0e7ef",
              background: "#fff",
              overflow: "hidden"
            }}>
              <Table sx={{ minWidth: 650 }} size="small">
                <TableHead>
                  <TableRow sx={{ background: "rgba(30,41,59,0.05)" }}>
                    <TableCell sx={{ 
                      fontWeight: 700, 
                      color: "rgba(30,41,59,0.98)",
                      borderBottom: "2px solid rgba(30,41,59,0.1)"
                    }}>
                      Código
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 700, 
                      color: "rgba(30,41,59,0.98)",
                      borderBottom: "2px solid rgba(30,41,59,0.1)"
                    }}>
                      Nombre
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 700, 
                      color: "rgba(30,41,59,0.98)",
                      borderBottom: "2px solid rgba(30,41,59,0.1)"
                    }}>
                      Descripción
                    </TableCell>
                    <TableCell 
                      align="right" 
                      sx={{ 
                        fontWeight: 700, 
                        color: "rgba(30,41,59,0.98)",
                        borderBottom: "2px solid rgba(30,41,59,0.1)"
                      }}
                    >
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTipos.map((tipo) => (
                    <TipoRow key={tipo.id} tipo={tipo} />
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}